/**
 * Rental SMS live updates: the General conversation and the rail SMS badge.
 *
 * Connects to /ws/sms/ (sms/consumers.py) on every page with the top-bar Messages envelope
 * (`[data-sms-envelope]`, templates/includes/_shell-topbar.html), and wherever the page holds a
 * General conversation root (`[data-general-conversation]`,
 * templates/propmgmt/rentals/detail/sms/_panel.html and _sheet.html) or a rental rail SMS badge
 * (`[data-rental-sms-unread]`, _rail_badge.html).
 * The socket model (backoff, slow retry, reconnect on `online` / tab visible, catch-up after
 * a reconnect) is copied from acqs-ws.js.
 *
 * On a new inbound text on a `general_propmgmt` line:
 *  - every open root whose picker lists the sender (`data-numbers`) re-renders from its
 *    `data-refresh-url` (`part=root`). Only a visible root marks the selected number read;
 *    a hidden one adds `mark_read=0`. Hidden covers a background tab, a closed section, and
 *    the panel while a sheet is open over it. A refresh made while the tab was in the
 *    background is redone with marking when the tab is shown again. A text that lands while
 *    a root's refresh is in flight refreshes that root once more after it settles.
 *    The composer sits outside the root, so a draft survives. When the refresh flips the
 *    root's `data-opted-out` (a STOP or START), the composer alone is re-rendered.
 *    A panel root's response carries the rail badge out of band; a sheet root's response
 *    sets it through `HX-Trigger: rentalSmsUnreadChanged`.
 *  - every other rail badge whose rental lists the sender (its `data-numbers`) refetches
 *    itself (`part=badge`).
 *
 * The badge also follows `rentalSmsUnreadChanged` from a sheet response (a picker switch or
 * a refresh), and refetches when sheets.js first loads a General sheet (sheets.js does
 * not apply response headers) or when the SMS rail section is reopened with unread texts.
 *
 * The Messages inbox (`[data-messages-inbox="<side>"]`, templates/propmgmt/messages_inbox/inbox.html)
 * uses the same socket, and refreshes only for the side it shows. Property Mgmt: a text on
 * any of its lines (General, Collections, Leasing), a General sheet opening (which marks the
 * thread read), and a rental sheet's `rentalSmsUnreadChanged`. Acquisitions: a text on an acqs
 * (Telegram relay) line, and the `{envelope: true}` ping, which an acqs-only user gets for such
 * a text, and which every user gets for it too (the `update` frame would carry a seller's number)
 * and when acqs texts are marked read (`sms.envelope.broadcast_envelope_changed`: a Telegram
 * `/send`, the lead conversation opening, or an acqs email count changing, which also refreshes
 * the Acquisitions email list). The ping refreshes whichever side is on screen. A
 * reconnect refreshes either side. Each fires `messagesInboxChanged` on body; the list's refresh
 * element re-requests the page with its filters (debounced there).
 *
 * The envelope (sms-envelope.js) hears a General `update`, the `envelope` ping, and a reconnect
 * as `sms:envelope-stale` on document. For a General text it fires
 * after the visible roots have re-rendered, so a text read on arrival is not counted. An `alert`
 * frame (a `pm_sms_alert` user's General text toast) goes on as `sms:alert` unless that number's
 * conversation is open: a visible root showing it, or the inbox's read-only General sheet. The
 * acqs email toast (`sms_user_<client>_<user>` group) is an `alert` with no `number`, so it
 * always goes on, even with its thread's sheet open.
 *
 * A `status` frame (`{status: {id}}`, an outbound text Twilio marked failed, undelivered or
 * canceled) re-renders that one entry inside any open acqs conversation
 * (`[data-acqs-conversation]`) or General conversation root, so "Undelivered" and the error
 * button show without a reload (`refreshFailedEntry`). It never refreshes the envelope, the
 * inbox, or a badge. A missed frame stays stale until the next refresh or reopen.
 */

(function () {
    var GENERAL_CHANNEL = 'general_propmgmt';
    var INBOX_CHANNELS = [GENERAL_CHANNEL, 'collections', 'leasing'];

    // `side` is 'propmgmt' or 'acqs'; omitted, either side refreshes.
    function refreshInbox(side) {
        var inbox = document.querySelector('[data-messages-inbox]');
        if (!window.htmx || !inbox) return;
        if (side && inbox.getAttribute('data-messages-inbox') !== side) return;
        htmx.trigger(document.body, 'messagesInboxChanged');
    }

    function roots() {
        return document.querySelectorAll('[data-general-conversation]');
    }

    function needsSocket() {
        return !!document.querySelector('[data-sms-envelope], [data-general-conversation], [data-rental-sms-unread], [data-messages-inbox]');
    }

    // The panel stays in the DOM while its section or the whole panel is hidden, and while a
    // sheet is open over it (`.sheet.show`; the panel's own `.sheet` never gets `show`). A
    // closed dynamic sheet lingers until its close transition ends.
    function isVisible(root) {
        if (document.hidden || root.closest('[hidden]')) return false;
        var openSheet = document.querySelector('.sheet.show');
        return openSheet ? openSheet.contains(root) : !!root.closest('.shell__panel');
    }

    // Set when a text is refreshed into a background tab without marking it read.
    var markWhenShown = false;

    // The General conversation with `number` is on screen: a visible root showing it, or the
    // inbox's read-only General sheet open on it.
    function conversationOpen(number) {
        var shown = Array.prototype.some.call(roots(), function (root) {
            return root.getAttribute('data-number') === number && isVisible(root);
        });
        var openSheet = document.querySelector('.sheet.show [data-inbox-general-sheet]');
        return shown || (!!openSheet && !document.hidden && openSheet.getAttribute('data-number') === number);
    }

    function envelopeStale() {
        document.dispatchEvent(new CustomEvent('sms:envelope-stale'));
    }

    function listsSender(el, from) {
        return (el.getAttribute('data-numbers') || '').split(/\s+/).indexOf(from) !== -1;
    }

    function rootIn(container) {
        return container.querySelector && container.querySelector('[data-general-conversation]');
    }

    function badgeFor(rentalId) {
        return document.getElementById('rental-sms-unread-' + rentalId);
    }

    function setBadge(rentalId, count) {
        var badge = badgeFor(rentalId);
        if (!badge) return;
        badge.textContent = count;
        badge.hidden = !count;
    }

    document.body.addEventListener('rentalSmsUnreadChanged', function (event) {
        setBadge(event.detail.rental_id, event.detail.count);
        refreshInbox('propmgmt');
    });

    function refreshBadge(rentalId) {
        if (!badgeFor(rentalId)) return;
        fetch('/propmgmt/rentals/' + rentalId + '/sms/?part=badge', { headers: { 'HX-Request': 'true' } })
            .then(function (r) {
                if (!r.ok) throw new Error('rental SMS badge ' + r.status);
                return r.text();
            })
            .then(function (html) {
                var tpl = document.createElement('template');
                tpl.innerHTML = html.trim();
                var next = tpl.content.querySelector('[data-rental-sms-unread]');
                var badge = badgeFor(rentalId);
                if (next && badge) setBadge(rentalId, parseInt(next.textContent, 10) || 0);
            })
            .catch(function (err) { console.warn('rental SMS badge refresh failed', err); });
    }

    // A `status` frame (sms.models.broadcast_failed_status): an outbound text failed. Each
    // entry with that `Message.pk` inside an open acqs or rental General conversation root is
    // re-rendered from the root's `data-refresh-url` (`mark_read=0`), by a plain fetch so the
    // response's out-of-band badge and `HX-Trigger` are ignored. Only that entry is replaced;
    // the timeline, scroll and composer stay. The leasing and collections chats reuse
    // `data-msg-id` for a log id, so the lookup never leaves the two roots. Skipped: a root
    // with no refresh URL (the Messages inbox sheets), an entry whose dropdown is open, and a
    // response without the entry (the root now shows another line or number).
    var STATUS_ROOTS = '[data-acqs-conversation], [data-general-conversation]';

    function entryMenuOpen(entry) {
        return Array.prototype.some.call(document.querySelectorAll('.dropdown-menu.show'), function (menu) {
            var owner = window._cc && window._cc.ownerOf ? window._cc.ownerOf(menu, '[data-msg-id]') : null;
            return owner === entry;
        });
    }

    function refreshFailedEntry(msgId) {
        var selector = '[data-msg-id="' + msgId + '"]';
        document.querySelectorAll(STATUS_ROOTS).forEach(function (root) {
            var url = root.getAttribute('data-refresh-url');
            var entry = root.querySelector(selector);
            if (!url || !entry || entryMenuOpen(entry)) return;
            // Nested roots: only the innermost one owns the entry.
            if (entry.closest(STATUS_ROOTS) !== root) return;
            fetch(url + '&mark_read=0', { headers: { 'HX-Request': 'true' } })
                .then(function (r) {
                    if (!r.ok) throw new Error('SMS entry refresh ' + r.status);
                    return r.text();
                })
                .then(function (html) {
                    var tpl = document.createElement('template');
                    tpl.innerHTML = html.trim();
                    var next = tpl.content.querySelector(selector);
                    if (!next || !entry.isConnected) return;
                    entry.replaceWith(next);
                    if (window.htmx) htmx.process(next);
                })
                .catch(function (err) { console.warn('SMS entry refresh failed', err); });
        });
    }

    // An open picker menu is portaled to <body>; replacing the root under it would orphan
    // the menu. The next text or a reconnect refreshes it.
    function pickerMenuOpen(root) {
        return Array.prototype.some.call(document.querySelectorAll('.dropdown-menu.show'), function (menu) {
            var owner = window._cc && window._cc.ownerOf ? window._cc.ownerOf(menu, '[data-general-conversation]') : null;
            return owner === root;
        });
    }

    // The surface response without `part=root` carries the composer: the panel's in the
    // out-of-band #rental-sms-footer, the sheet's in its footer. `data-composer-oob` picks it
    // out and `swap: 'none'` leaves the root alone.
    // The panel footer arrives without `hidden`, and shell.js sets footer visibility only when
    // a section opens, so the swap re-applies its rule: shown only for the open section
    // (`data-panel-current`). The swap runs inside the response handler, so this lands before
    // the next paint, and it runs on failure too. The request's source is the composer's own
    // element, not the root: htmx queues requests per source, and the root's follow-up
    // refresh must not wait behind this one.
    function refreshComposer(root) {
        var selector = root.getAttribute('data-composer-oob');
        var url = new URL(root.getAttribute('data-refresh-url'), location.href);
        url.searchParams.delete('part');
        url.searchParams.set('mark_read', '0');
        var source = document.querySelector(selector) || document.body;
        function applyFooterVisibility() {
            var footer = document.querySelector(selector);
            var panel = footer && footer.hasAttribute('data-panel-footer') && footer.closest('#shell-panel');
            if (panel) footer.hidden = footer.getAttribute('data-panel-footer') !== panel.getAttribute('data-panel-current');
        }
        htmx.ajax('GET', url.pathname + url.search, { source: source, swap: 'none', selectOOB: selector })
            .then(applyFooterVisibility, applyFooterVisibility);
    }

    // Root id -> { pending, markRead } while that root's refresh is in flight. htmx queues a
    // second request on the same root and drops it once the first swap detaches that root, so
    // a text landing meanwhile is recorded here and refreshed again from the new root.
    var inFlight = Object.create(null);

    // Returns a promise that settles after the refresh and any follow-up, or null when no
    // request went out. htmx.ajax applies the panel's out-of-band badge and the sheet's
    // HX-Trigger. `select` keeps only the root, whatever an ancestor inherits.
    function refreshRoot(root, markRead) {
        var url = root.getAttribute('data-refresh-url');
        if (!url || !window.htmx || !root.id || pickerMenuOpen(root)) return null;
        var id = root.id;
        var state = inFlight[id];
        if (state) {
            state.pending = true;
            state.markRead = state.markRead || markRead;
            return state.promise;
        }
        state = inFlight[id] = { pending: false, markRead: false };
        if (!markRead || !isVisible(root)) {
            url += '&mark_read=0';
            if (markRead && document.hidden) markWhenShown = true;
        }
        var optedOut = root.getAttribute('data-opted-out');
        var number = root.getAttribute('data-number');
        // A root for another number is the user's picker switch, which brought its own composer.
        function settled() {
            delete inFlight[id];
            var next = document.getElementById(id);
            if (!next) return;
            if (next !== root && next.getAttribute('data-number') === number
                && next.getAttribute('data-opted-out') !== optedOut) refreshComposer(next);
            if (state.pending) return refreshRoot(next, state.markRead);
        }
        state.promise = htmx.ajax('GET', url, { source: root, target: root, select: '#' + id, swap: 'outerHTML' })
            .then(settled, settled);
        return state.promise;
    }

    // `from` is null on a reconnect: every root and badge catches up once. Visible roots go
    // first: they mark read, and a hidden panel's badge counted before that would come back
    // stale (a sheet open over the page with the panel's SMS section hidden under it).
    function refreshForInbound(from) {
        var covered = Object.create(null);
        var visible = [];
        var hidden = [];
        roots().forEach(function (root) {
            if (from && !listsSender(root, from)) return;
            if (!root.getAttribute('data-refresh-url') || pickerMenuOpen(root)) return;
            covered[root.getAttribute('data-rental-id')] = true;
            (isVisible(root) ? visible : hidden).push(root);
        });
        if (document.hidden && hidden.length) markWhenShown = true;
        var marked = Promise.all(visible.map(function (root) { return refreshRoot(root, true); }));
        marked.then(function () {
            hidden.forEach(function (root) { refreshRoot(root, false); });
        });
        document.querySelectorAll('[data-rental-sms-unread]').forEach(function (badge) {
            var rentalId = badge.getAttribute('data-rental-sms-unread');
            if (covered[rentalId]) return;
            if (from && !listsSender(badge, from)) return;
            refreshBadge(rentalId);
        });
        return marked;
    }

    // sheets.js loads a sheet with its own XHR and ignores HX-Trigger, so the badge refetches
    // after the sheet's GET has marked the number read.
    document.addEventListener('sheet:ready', function (event) {
        var root = rootIn(event.target);
        if (root) refreshBadge(root.getAttribute('data-rental-id'));
        if (root || event.target.querySelector('[data-inbox-general-sheet]')) refreshInbox('propmgmt');
    });

    // The SMS section loads once (`intersect once`). Reopening it with unread texts that
    // arrived while it was hidden re-renders the root, which marks the selected number read.
    document.addEventListener('shell:panel-open', function (event) {
        var root = rootIn(event.target);
        if (!root) return;
        var badge = badgeFor(root.getAttribute('data-rental-id'));
        if (badge && !badge.hidden) refreshRoot(root, true);
    });

    // ── WebSocket connection ───────────────────────────────────────────────

    var socket = null;
    var reconnectTimer = null;
    var reconnectAttempts = 0;
    var SLOW_RECONNECT_MS = 60000;
    // Set just before `checkConnection` closes the socket on purpose, cleared by the next
    // open. A deliberate close missed nothing the next page cares about.
    var deliberateClose = false;
    // The first connect needs no catch-up; the server rendered the page moments earlier.
    var hasConnected = false;

    function clearReconnectTimer() {
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    }

    function connect() {
        clearReconnectTimer();

        var protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
        var ws = socket = new WebSocket(protocol + '//' + location.host + '/ws/sms/');

        socket.onopen = function () {
            var missedEvents = hasConnected && !deliberateClose;
            reconnectAttempts = 0;
            hasConnected = true;
            deliberateClose = false;
            if (missedEvents) {
                refreshForInbound(null).then(envelopeStale, envelopeStale);
                refreshInbox();
            }
        };

        socket.onmessage = function (e) {
            var data = JSON.parse(e.data);
            // Before the no-`update` branch: a status frame never touches the envelope or inbox.
            if (data.status) {
                refreshFailedEntry(data.status.id);
                return;
            }
            if (data.alert) {
                if (!conversationOpen(data.alert.number)) {
                    document.dispatchEvent(new CustomEvent('sms:alert', { detail: data.alert }));
                }
                return;
            }
            var update = data.update;
            // The `{envelope: true}` ping (an acqs-line text, or acqs texts marked read) refreshes
            // whichever inbox side is open. Of the `update`s, only General (counted after the
            // read, below) can change a count; Collections and Leasing texts never do.
            if (!update) {
                envelopeStale();
                refreshInbox();
                return;
            }
            if (INBOX_CHANNELS.indexOf(update.channel) !== -1) refreshInbox('propmgmt');
            if (update.channel !== GENERAL_CHANNEL) return;
            refreshForInbound(update.from).then(envelopeStale, envelopeStale);
        };

        socket.onclose = function (e) {
            // A deliberate close has already let go of this socket, and a new one may be open.
            if (socket !== ws) return;
            socket = null;
            if (e.code === 4401) {
                window.location.href = '/login/?next=' + encodeURIComponent(window.location.pathname);
                return;
            }
            // 4403: no client, or neither inbox side (`propmgmt.rentals` view, `acqs.acqs-lead` view). Retrying cannot change that.
            if (e.code === 4403 || !needsSocket()) return;
            // Back off to 30s over ten attempts, then once a minute while the tab is visible;
            // a hidden tab waits for `visibilitychange`, and `online` or a visible tab reconnects at once
            // (`reconnectNow`).
            if (reconnectAttempts >= 10 && document.hidden) return;
            var delay = reconnectAttempts < 10
                ? Math.min(2000 * Math.pow(1.5, reconnectAttempts), 30000)
                : SLOW_RECONNECT_MS;
            reconnectAttempts++;
            reconnectTimer = setTimeout(connect, delay);
        };
    }

    // `online` and a tab shown again: the wait is pointless, so drop any pending backoff.
    function reconnectNow() {
        if (!needsSocket() || socket) return;
        clearReconnectTimer();
        reconnectAttempts = 0;
        connect();
    }

    // Connect and disconnect as htmx navigation, sheets and swaps add or remove the markers.
    function checkConnection() {
        var wanted = needsSocket();
        // A pending reconnect keeps its backoff: every page swap would otherwise retry at once.
        if (wanted && !socket && !reconnectTimer) {
            reconnectAttempts = 0;
            connect();
        } else if (!wanted && socket) {
            deliberateClose = true;
            socket.close();
            socket = null;
            clearReconnectTimer();
        }
    }

    checkConnection();
    document.body.addEventListener('htmx:pushedIntoHistory', checkConnection);
    window.addEventListener('popstate', checkConnection);
    document.body.addEventListener('htmx:afterSettle', checkConnection);
    document.addEventListener('sheet:ready', checkConnection);
    window.addEventListener('online', reconnectNow);
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState !== 'visible') return;
        reconnectNow();
        checkConnection();
        if (markWhenShown) {
            markWhenShown = false;
            refreshForInbound(null);
        }
    });

})();
